import { defineStore, type _GettersTree } from 'pinia'
import type { IStickyBannerProps } from 'components/sticky-banner/types'
import type { VNode } from 'vue'
import {
  useInterpolation,
  type InterpolationRunner
} from '@/utils/animation/useInterpolation'
import { isNotNullish } from '@/utils/is'
import { hashAny } from '@/utils/hash'

export type StickyBannerObject = {
  uid: string
  props: IStickyBannerProps
  animation: InterpolationRunner;
  node?: VNode
}

type StickyBannerMap = { [uid: string]: StickyBannerObject | undefined }

export interface IStickyBannerStoreState {
  stickyBanners: StickyBannerMap //IStickyBannerPropsInternal[]
  stack: string[]
}

export interface IStickyBannerStoreActions {
  pushBanner: (banner: IStickyBannerProps) => void;
  popBanner: (uid?: string) => Promise<void>;
  clearBanners: () => void
}

export interface IStickyBannerStoreGetters extends _GettersTree<any> {
  count: () => number
}

export const useStickyBannerStore = defineStore<
  'stickyBanner',
  IStickyBannerStoreState,
  IStickyBannerStoreGetters,
  IStickyBannerStoreActions
>('stickyBanner', {
  state() {
    return {
      stickyBanners: {},
      stack: [],
      stickyBannerRect: { x: 0, y: 0, width: 0, height: 0 }
    }
  },
  getters: {
    count() {
      return Object.values(this.stickyBanners).length
    }
  },
  actions: {
    clearBanners() {
      const keys = Object.keys(this.stickyBanners).filter(isNotNullish)
      if (keys.length <= 0) return

      keys.forEach((key) => this.popBanner(key));

    },
    pushBanner(stickyBanner) {
      const uid = stickyBanner.uid || hashAny(stickyBanner) //v4();

      if (this.stickyBanners[uid]) return


      const animation = useInterpolation({
        initial: 0,
        duration: 0.5
      })

      const stickyBannerObject: StickyBannerObject = {
        uid,
        props: {
          ...stickyBanner,
          uid: uid,
          onVnodeMounted: () => {
            animation.runTo(1.0);
          }
        },
        animation: animation
      }

      this.$patch(s => {
        s.stickyBanners[uid] = stickyBannerObject;
        s.stack = [...s.stack, uid];
      })

    },
    async popBanner(uid?: string) {
      const keys = this.$state.stack;
      if (keys.length <= 0) return;
      const key = uid || this.$state.stack[this.$state.stack.length-1];
      if (!key) return;
      const banner = this.$state.stickyBanners[key];
      if (!banner) return;
      await banner.animation.runTo(0.0);

      this.$patch(s => {
        s.stack = s.stack.filter(k => k !== key);
        banner.animation.cancel();
        delete s.stickyBanners[key];
      })
    }
  }
})


export const pushBanner = (stickyBanner: IStickyBannerProps) => {
  const bannerStore = useStickyBannerStore();
  bannerStore.pushBanner(stickyBanner);
}
